import {
    Button,
    FormLabel,
    FormErrorMessage,
    Flex,
    Input,
    FormControl,
    Select,
    Box,
    FormHelperText,
    Alert,
    AlertIcon,
    Textarea,
    Tooltip,
    CloseButton,
} from "@chakra-ui/react";
import { FileContainer, FileUploader, SingleSelectControlled, useConfirm } from "am-tax-fe-core";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { LinkedClient, TransformedClientData } from "../../../utils/types";
import { ALLOWED_DIAGRAM_EXTENSIONS } from "../../../utils/constants";
// import { useDeleteIndustryAnalysisDiagram, useGetClientIndustryAnalysisFiles } from "../../../api/clients/clientQueries";
import { useGetUnlinkedClients, useCreateClient, useUpdateClient, useDeleteClient, useUploadOrgDiagram } from "../../../api/clients/clientQueries";
// import { uploadIndustryAnalysisFileAsync } from "../../../api/clients/clientApi";
import { LEADMD_LIST } from "../../../utils/constants";

interface Props {
    onClose: () => void;
    clientData: TransformedClientData | undefined;
    existingClients: TransformedClientData[] | undefined;
    isEdit: boolean;
}

type FormFields = {
    clientName: string;
    agressoCode: string;
    customAggressoCode?: string;
    leadMD: string;
    abbreviation: string;
    industryAnalysis: string;
    orgDiagram: string;
    headcount: string;
    description: string;
    endingDate: string;
    industry: string;
    overviewOfIndustry: string;
    productsAndServices: string;
    majorPlayers: string;
    industryPerformance: string;
    competitiveLandscape: string;
    keyExternalDrivers: string;
    barriersToEntry: string;
};

export const CreateOrEditClientForm = ({ clientData, isEdit, onClose }: Props) => {
    const { data: unlinkedClients, isLoading: isLoadingUnlinkedClients } = useGetUnlinkedClients();
    const createClientMutation = useCreateClient();
    const updateClientMutation = useUpdateClient();
    const deleteClientMutation = useDeleteClient();
    const agressoCodes = ["12-3456789", "98-7654321", "Other"];

    const allClients: LinkedClient[] =
        unlinkedClients?.map(unlinkedClient => ({
            id: unlinkedClient.clientId,
            name: unlinkedClient.clientName,
            code: unlinkedClient.roleUrn,
            abbreviation: unlinkedClient.accessLevel,
            leadMD: unlinkedClient.roleName,
        })) || [];

    // Check if client selection is made for new clients (not edit mode)
    const hasNoUnlinkedClients = !isLoadingUnlinkedClients && (!allClients || allClients.length === 0);
    const [selectedClientId, setSelectedClientId] = useState<string>(clientData?.id || "");
    const isClientSelected = isEdit || selectedClientId !== "";
    const shouldDisableFields = !isEdit && !isClientSelected;

    const formValues = {
        clientName: clientData?.name ?? "",
        agressoCode: clientData?.agressoCode ? (agressoCodes.includes(clientData!.agressoCode) ? clientData?.agressoCode : "Other") || "" : "",
        customAggressoCode: clientData?.agressoCode ? (agressoCodes.includes(clientData!.agressoCode) ? "" : clientData?.agressoCode) || "" : "",
        leadMD: clientData?.leadMD ?? "",
        abbreviation: clientData?.abbreviation ?? "",
        industryAnalysis: "",
        orgDiagram: "",
        headcount: clientData?.headcount ?? "",
        description: clientData?.description ?? "",
        endingDate: clientData?.endingDate ?? "",
        industry: clientData?.industry || "",
        overviewOfIndustry: clientData?.overviewOfIndustry || "",
        productsAndServices: clientData?.productsAndServices || "",
        majorPlayers: clientData?.majorPlayers || "",
        industryPerformance: clientData?.industryPerformance || "",
        competitiveLandscape: clientData?.competitiveLandscape || "",
        keyExternalDrivers: clientData?.keyExternalDrivers || "",
        barriersToEntry: clientData?.barriersToEntry || "",
    };

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        setError,
        clearErrors,
        control,
        watch,
    } = useForm<FormFields>({ values: formValues });

    const prompt = "Are you sure you want to delete the client?";
    const { ConfirmDialog } = useConfirm({ title: "Delete Client", prompt: prompt });
    const [selectedOrgDiagramFile, setSelectedOrgDiagramFile] = useState<FileContainer | undefined>();
    // const [selectedIndustryAnalysisFiles, setSelectedIndustryAnalysisFiles] = useState<FileContainer[]>();
    const { mutate: uploadOrgDiagram } = useUploadOrgDiagram();
    const [hasOrgDiagram, setHasOrgDiagram] = useState<boolean>(!!clientData?.orgDiagram && clientData?.orgDiagram.split("/").length > 1);
    // const [industryAnalysisFileList, setIndustryAnalysisFileList] = useState<string[]>([]);
    // const { data: industryAnalysisFiles, refetch } = useGetClientIndustryAnalysisFiles({ clientId: clientData?.id });
    // const { mutate: deleteIndustryAnalysisDiagram } = useDeleteIndustryAnalysisDiagram();
    const [fileErrors, setFileErrors] = useState<("industryAnalysis" | "orgDiagram")[]>([]);
    const [selectedSectionIndex, setSelectedSectionIndex] = useState<number>(0);

    // useEffect(() => {
    //     refetch();
    // }, [refetch]);

    // useEffect(() => {
    //     if (industryAnalysisFiles?.fileNames) {
    //         setIndustryAnalysisFileList(industryAnalysisFiles.fileNames);
    //     }
    // }, [industryAnalysisFiles]);

    const handleDelete = async () => {
        const confirmed = await confirm("Are you sure you want to delete this client?");
        if (!confirmed) return;

        if (!clientData?.id) {
            console.error("No client ID provided for deletion");
            return;
        }

        try {
            await deleteClientMutation.mutateAsync(clientData.id);
            onClose();
        } catch (error) {
            console.error("Error deleting client:", error);
        }
    };

    const onSubmit: SubmitHandler<FormFields> = async data => {
        try {
            let clientId = clientData?.id;
            if (fileErrors.length > 0) {
                // Prevent submit if there are validation errors
                for (const error of fileErrors) {
                    {
                        setError(error, {
                            type: "validate", // Use "validate" so error persists until valid input
                            message: "Invalid file format.",
                        });
                    }
                }
                return;
            }

            if (!isEdit) {
                // Find the selected client to get both ID and name
                const selectedClient = allClients.find(client => client.id === data.clientName);

                if (!selectedClient) {
                    console.error("Selected client not found");
                    return;
                }

                // Create new client
                const createClientData = {
                    id: selectedClient.id,
                    name: selectedClient.name, // Add the client name from Engage
                    agressoCode: data.agressoCode === "Other" ? data.customAggressoCode! : data.agressoCode!,
                    leadMD: data.leadMD,
                    abbreviation: data.abbreviation,
                    headcount: data.headcount,
                    description: data.description,
                    endingDate: data.endingDate,
                    industry: data.industry,
                    overviewOfIndustry: data.overviewOfIndustry,
                    productsAndServices: data.productsAndServices,
                    majorPlayers: data.majorPlayers,
                    industryPerformance: data.industryPerformance,
                    competitiveLandscape: data.competitiveLandscape,
                    keyExternalDrivers: data.keyExternalDrivers,
                    barriersToEntry: data.barriersToEntry,
                };

                clientId = (await createClientMutation.mutateAsync(createClientData)).id;
            } else {
                // Update existing client
                if (!clientData?.id) {
                    console.error("No client ID provided for update");
                    return;
                }

                const updateClientData = {
                    id: clientData.id,
                    name: clientData.name, // Keep existing name from database
                    agressoCode: data.agressoCode === "Other" ? data.customAggressoCode! : data.agressoCode!,
                    leadMD: data.leadMD,
                    abbreviation: data.abbreviation,
                    industryAnalysisBlobPath: clientData.industryAnalysis,
                    orgDiagramBlobPath: clientData.orgDiagram,
                    headcount: data.headcount,
                    description: data.description,
                    endingDate: data.endingDate,
                    industry: data.industry,
                    overviewOfIndustry: data.overviewOfIndustry,
                    productsAndServices: data.productsAndServices,
                    majorPlayers: data.majorPlayers,
                    industryPerformance: data.industryPerformance,
                    competitiveLandscape: data.competitiveLandscape,
                    keyExternalDrivers: data.keyExternalDrivers,
                    barriersToEntry: data.barriersToEntry,
                };

                await updateClientMutation.mutateAsync(updateClientData);
            }

            if (selectedOrgDiagramFile) {
                await uploadOrgDiagram({ file: selectedOrgDiagramFile, clientId: clientId ?? "" });
            }

            onClose();

            // if (selectedIndustryAnalysisFiles && selectedIndustryAnalysisFiles.length > 0) {
            //     for (const file of selectedIndustryAnalysisFiles) {
            //         await uploadIndustryAnalysisFileAsync(file, clientId ?? "");
            //     }
            // }
        } catch (error) {
            console.error("Error uploading org diagram:", error);
        }
    };

    const handleRemoveOrgDiagram = () => {
        setSelectedOrgDiagramFile(undefined);
        setHasOrgDiagram(false);
        setValue("orgDiagram", "");
        clearErrors("orgDiagram");
    };

    const onFileOrgAdded = async (fileContainer: FileContainer) => {
        const fileExtension = fileContainer.file.type.split("/")[1];

        if (ALLOWED_DIAGRAM_EXTENSIONS.includes(fileExtension)) {
            setSelectedOrgDiagramFile(fileContainer);
            clearErrors("orgDiagram");
            setFileErrors(fileErrors?.filter(error => error !== "orgDiagram") || []);
        } else {
            console.error("Invalid file format detected in onFileOrgAdded. File extension:", fileExtension);
            setError("orgDiagram", {
                type: "validate", // Use "validate" so error persists until valid input
                message: "Invalid file format.",
            });
            setFileErrors(prev => (prev?.includes("orgDiagram") ? prev : [...(prev || []), "orgDiagram"]));
        }
    };

    const onFileOrgCancelled = async () => {
        setSelectedOrgDiagramFile(undefined);
        setFileErrors(fileErrors?.filter(error => error !== "orgDiagram") || []);
        clearErrors("orgDiagram");
    };

    // const onFileIndustryAnalysisAdded = async (fileContainer: FileContainer) => {
    //     const fileExtension = fileContainer.file.type.split("/")[1];

    //     if (ALLOWED_DIAGRAM_EXTENSIONS.includes(fileExtension)) {
    //         setSelectedIndustryAnalysisFiles(prevFiles => [...(prevFiles || []), fileContainer]);
    //         clearErrors("industryAnalysis");
    //         setFileErrors(fileErrors?.filter(error => error !== "industryAnalysis") || []);
    //     } else {
    //         setError("industryAnalysis", {
    //             type: "validate", // Use "validate" so error persists until valid input
    //             message: "Invalid file format.",
    //         });
    //         setFileErrors(prev => (prev?.includes("industryAnalysis") ? prev : [...(prev || []), "industryAnalysis"]));
    //     }
    // };

    // const onFileIndustryAnalysisCancelled = (fileContainer: FileContainer) => {
    //     setSelectedIndustryAnalysisFiles(prevFiles => prevFiles?.filter(file => file.file.name !== fileContainer.file.name) || []);
    //     setFileErrors(fileErrors?.filter(error => error !== "industryAnalysis") || []);
    //     clearErrors("industryAnalysis");
    // };

    // const onRemoveExistingIndustryAnalysisFile = async (fileName: string) => {
    //     await deleteIndustryAnalysisDiagram({
    //         clientId: clientData?.id || "",
    //         fileName: fileName,
    //     });

    //     await refetch();

    //     setIndustryAnalysisFileList(prevFiles => prevFiles?.filter(file => file !== fileName) || []);
    //     clearErrors("industryAnalysis");
    // };

    return (
        <>
            <Flex mb="1.5rem" gap="0.5rem">
                <Button variant={selectedSectionIndex === 0 ? "solid" : "outline"} onClick={() => setSelectedSectionIndex(0)}>
                    Main Fields
                </Button>
                <Button variant={selectedSectionIndex === 1 ? "solid" : "outline"} onClick={() => setSelectedSectionIndex(1)}>
                    Industry Analysis
                </Button>
            </Flex>
            <form onSubmit={handleSubmit(onSubmit)}>
                {selectedSectionIndex === 0 ? (
                    <Flex gap="2rem" flexFlow="column nowrap">
                        {!isEdit && hasNoUnlinkedClients && (
                            <Alert status="info" borderRadius="md">
                                <AlertIcon />
                                All of your Engage clients have already been provisioned. No additional clients are available to add at this time.
                            </Alert>
                        )}

                        <Flex gap="1rem">
                            {!isEdit ? (
                                <FormControl isInvalid={!!errors?.clientName} isRequired>
                                    <FormLabel>Engage Client:</FormLabel>
                                    <Select
                                        {...register("clientName", {
                                            required: "Please select a client",
                                        })}
                                        placeholder={
                                            isLoadingUnlinkedClients ? "Loading clients..." : hasNoUnlinkedClients ? "No clients available" : "Select Client"
                                        }
                                        disabled={isLoadingUnlinkedClients || hasNoUnlinkedClients}
                                        onChange={e => {
                                            setSelectedClientId(e.target.value);
                                        }}
                                    >
                                        {allClients?.map(client => (
                                            <option key={client.id} value={client.id}>
                                                {client.name}
                                            </option>
                                        ))}
                                    </Select>
                                    <FormErrorMessage>{errors.clientName?.message}</FormErrorMessage>
                                    {!isClientSelected && (
                                        <FormHelperText color="gray.500">Please select a client to continue filling out the form.</FormHelperText>
                                    )}
                                </FormControl>
                            ) : (
                                <FormControl>
                                    <FormLabel>Client Name:</FormLabel>
                                    <Box>
                                        <Input readOnly {...register("clientName")} />
                                    </Box>
                                </FormControl>
                            )}
                            <FormControl isInvalid={!!errors?.agressoCode} isRequired>
                                <FormLabel>Agresso Code:</FormLabel>
                                <SingleSelectControlled
                                    control={control}
                                    options={agressoCodes.map(opt => ({ value: opt, label: opt }))}
                                    name={"agressoCode"}
                                    isDisabled={shouldDisableFields}
                                />
                                <FormErrorMessage>{errors.agressoCode?.message}</FormErrorMessage>
                            </FormControl>
                        </Flex>

                        {watch("agressoCode") === "Other" && (
                            <FormControl isInvalid={!!errors?.agressoCode} isRequired={watch("agressoCode") === "Other"}>
                                <FormLabel>Custom agresso code:</FormLabel>
                                <Input
                                    {...register("customAggressoCode", {
                                        required: "Agresso code is required",
                                        validate: value => value?.trim() !== "" || "Agresso code is required",
                                    })}
                                    disabled={shouldDisableFields}
                                />
                                <FormErrorMessage>{errors.customAggressoCode?.message}</FormErrorMessage>
                            </FormControl>
                        )}

                        <Flex gap="1rem">
                            <FormControl isInvalid={!!errors?.leadMD} isRequired>
                                <FormLabel>Lead MD:</FormLabel>
                                <Select
                                    {...register("leadMD", {
                                        required: "Lead MD is required.",
                                    })}
                                    placeholder="Select Lead MD"
                                    disabled={shouldDisableFields}
                                >
                                    {LEADMD_LIST.map(md => (
                                        <option key={md} value={md}>
                                            {md}
                                        </option>
                                    ))}
                                </Select>
                                <FormErrorMessage>{errors.leadMD?.message}</FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={!!errors?.abbreviation} isRequired>
                                <FormLabel>Client Abbreviation:</FormLabel>
                                <Input
                                    {...register("abbreviation", {
                                        required: "Client Abbreviation is required.",
                                        validate: value => value.trim() !== "" || "Client Abbreviation is required.",
                                    })}
                                    disabled={shouldDisableFields}
                                />
                                <FormErrorMessage>{errors.abbreviation?.message}</FormErrorMessage>
                            </FormControl>
                        </Flex>

                        <Flex gap="1rem">
                            <FormControl isInvalid={!!errors?.endingDate} isRequired>
                                <FormLabel>Ending Date:</FormLabel>
                                <Input
                                    type="date"
                                    {...register("endingDate", {
                                        required: "Ending date is required.",
                                    })}
                                    value={
                                        watch("endingDate") && !isNaN(new Date(watch("endingDate")).getTime())
                                            ? new Date(watch("endingDate")).toISOString().slice(0, 10)
                                            : ""
                                    }
                                />
                                <FormErrorMessage>{errors.endingDate?.message}</FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={!!errors?.headcount}>
                                <FormLabel>Headcount:</FormLabel>
                                <Input
                                    type="number"
                                    min={0}
                                    step={1}
                                    {...register("headcount", {
                                        validate: value =>
                                            value.trim() === "" || (!isNaN(Number(value)) && Number(value) > 0) ? true : "Headcount must be a positive number.",
                                    })}
                                    disabled={shouldDisableFields}
                                />
                                <FormErrorMessage>{errors.headcount?.message}</FormErrorMessage>
                            </FormControl>
                        </Flex>

                        <FormControl isInvalid={!!errors?.description} isRequired>
                            <FormLabel>Description:</FormLabel>
                            <Input
                                {...register("description", {
                                    required: "Description is required.",
                                    validate: value => value.trim() !== "" || "Description is required.",
                                })}
                                disabled={shouldDisableFields}
                            />
                            <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={!!errors?.industry}>
                            <FormLabel>Industry:</FormLabel>
                            <Input {...register("industry")} disabled={shouldDisableFields} />
                            <FormErrorMessage>{errors.industry?.message}</FormErrorMessage>
                        </FormControl>

                        {/* Org Diagram commented out for now. They aren't using it. */}
                        {hasOrgDiagram ? (
                            <Box mt="2rem">
                                <FormLabel>Org. Diagram File:</FormLabel>
                                <Flex align="center" mt="1rem" mb="1rem">
                                    <p>{clientData?.orgDiagram?.split("/").pop()}</p>
                                    <Tooltip label="Remove org diagram">
                                        <CloseButton ml="1rem" variant="ghost" aria-label="Close" onClick={() => handleRemoveOrgDiagram()} />
                                    </Tooltip>
                                </Flex>
                            </Box>
                        ) : (
                            <Box mt="2rem">
                                <FormControl isInvalid={!!errors?.orgDiagram}>
                                    <FormLabel mt="2rem">Upload Org. Diagram:</FormLabel>

                                    <Flex direction="column" gap={5}>
                                        <FileUploader
                                            {...register("orgDiagram")}
                                            maxSize={10485760}
                                            maxFiles={1}
                                            onFileAdded={onFileOrgAdded}
                                            onFileCancelled={onFileOrgCancelled}
                                            disabled={shouldDisableFields}
                                            accept={{
                                                "image/png": [".png"],
                                                "image/jpeg": [".jpg", ".jpeg"],
                                                "image/svg+xml": [".svg"],
                                            }}
                                        />
                                    </Flex>
                                    <FormErrorMessage>{errors.orgDiagram?.message}</FormErrorMessage>
                                </FormControl>
                            </Box>
                        )}

                        {/* {industryAnalysisFileList && industryAnalysisFileList.length > 0 ? (
                            <Box mt="2rem">
                                <FormLabel>Industry Analysis Diagrams:</FormLabel>
                                {industryAnalysisFileList.map(file => (
                                    <Flex key={file} align="center" mt="1rem" mb="1rem">
                                        <p>{file.split("/").pop()}</p>
                                        <Tooltip label="Remove industry analysis file">
                                            <CloseButton
                                                ml="1rem"
                                                variant="ghost"
                                                aria-label="Close"
                                                onClick={() => onRemoveExistingIndustryAnalysisFile(file)}
                                            />
                                        </Tooltip>
                                    </Flex>
                                ))}
                            </Box>
                        ) : (
                            <FormControl isInvalid={!!errors?.industryAnalysis}>
                                <FormLabel>Industry Analysis Diagrams:</FormLabel>

                                <Flex direction="column" gap={5}>
                                    <FileUploader
                                        {...register("industryAnalysis")}
                                        maxSize={10485760}
                                        onFileCancelled={onFileIndustryAnalysisCancelled}
                                        onFileAdded={onFileIndustryAnalysisAdded}
                                        disabled={shouldDisableFields}
                                    />
                                </Flex>
                                <FormHelperText>Upload a PDF or image file (png, jpg, svg, etc).</FormHelperText>
                                <FormErrorMessage>{errors.industryAnalysis?.message}</FormErrorMessage>
                            </FormControl>
                        )} */}

                        <Flex marginBottom=".75rem" justifyContent="space-between">
                            {isEdit && (
                                <Button variant="ghost" colorScheme="red" onClick={handleDelete} isLoading={deleteClientMutation.isPending}>
                                    Delete Client
                                </Button>
                            )}
                            <Button
                                type="submit"
                                variant="primary"
                                isLoading={isEdit ? updateClientMutation.isPending : createClientMutation.isPending}
                                disabled={shouldDisableFields || hasNoUnlinkedClients}
                            >
                                {isEdit ? "Update" : "Add"}
                            </Button>
                        </Flex>
                    </Flex>
                ) : (
                    <Flex gap="2rem" mb="1rem" flexFlow="column nowrap">
                        <FormControl isInvalid={!!errors?.overviewOfIndustry}>
                            <FormLabel>Overview of Industry:</FormLabel>
                            <Textarea {...register("overviewOfIndustry")} disabled={shouldDisableFields} />
                            <FormErrorMessage>{errors.overviewOfIndustry?.message}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={!!errors?.productsAndServices}>
                            <FormLabel>Products / Services:</FormLabel>
                            <Textarea {...register("productsAndServices")} disabled={shouldDisableFields} />
                            <FormErrorMessage>{errors.productsAndServices?.message}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={!!errors?.majorPlayers}>
                            <FormLabel>Major Players:</FormLabel>
                            <Textarea {...register("majorPlayers")} disabled={shouldDisableFields} />
                            <FormErrorMessage>{errors.majorPlayers?.message}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={!!errors?.industryPerformance}>
                            <FormLabel>Industry Performance:</FormLabel>
                            <Textarea {...register("industryPerformance")} disabled={shouldDisableFields} />
                            <FormErrorMessage>{errors.industryPerformance?.message}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={!!errors?.competitiveLandscape}>
                            <FormLabel>Competitive Landscape:</FormLabel>
                            <Textarea {...register("competitiveLandscape")} disabled={shouldDisableFields} />
                            <FormErrorMessage>{errors.competitiveLandscape?.message}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={!!errors?.keyExternalDrivers}>
                            <FormLabel>Key External Drivers:</FormLabel>
                            <Textarea {...register("keyExternalDrivers")} disabled={shouldDisableFields} />
                            <FormErrorMessage>{errors.keyExternalDrivers?.message}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={!!errors?.barriersToEntry}>
                            <FormLabel>Barriers to Entry:</FormLabel>
                            <Textarea {...register("barriersToEntry")} disabled={shouldDisableFields} />
                            <FormErrorMessage>{errors.barriersToEntry?.message}</FormErrorMessage>
                        </FormControl>
                    </Flex>
                )}
            </form>
            <ConfirmDialog />
        </>
    );
};
